import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  CurrencyExchange,
  MoneyOff,
} from '@mui/icons-material';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent } from '@ionic/react';
import { 
  addCryptoCurrencyAPI, 
  getCryptoCurrenciesAPI, 
  updateCryptoCurrencyAPI, 
  deleteCryptoCurrencyAPI 
} from '../../../api/orders/orders';
import { MenuButton } from '../../../components/Menu';

interface CryptoCurrency {
  id: number;
  name: string;
  symbol: string;
  business_id: string;
  dateCreated: string;
  hasRateBox: boolean; // New field to determine if crypto has Value Box
  rateBoxLabel?: string; // Optional label for the Value Box
}

const SetupCryptoType: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [cryptoCurrencies, setCryptoCurrencies] = useState<CryptoCurrency[]>([]);
  const [currencies, setCurrencies] = useState<CryptoCurrency[]>([]);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<CryptoCurrency | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    business_id: 'business_001', // Default business ID
    hasRateBox: false, // Default to having Value Box
    rateBoxLabel: '', // Default empty Crypto Type
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch cryptocurrencies on component mount
  useEffect(() => {
    fetchCryptoCurrencies();
  }, []);

  // Filter cryptocurrencies based on search term
  const filteredCurrencies = currencies?.filter(currency =>
    currency.name.toLowerCase().includes(searchTerm?.toLowerCase()) ||
    currency.symbol.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const fetchCryptoCurrencies = async () => {
    setLoading(true);
    try {
      const response = await getCryptoCurrenciesAPI();
      setCryptoCurrencies(response.data);
      setCurrencies(response.data);
      console.log("Fetched cryptocurrencies:", response.data);
    } catch (error) {
      console.error("Error fetching cryptocurrencies:", error);
      showSnackbar('Failed to load cryptocurrencies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCurrency = async () => {
    if (!formData.name) {
      showSnackbar('Name is required', 'error');
      return;
    }

    // If hasRateBox is true but rateBoxLabel is empty, show warning
    if (formData.hasRateBox && !formData.rateBoxLabel.trim()) {
      showSnackbar('Crypto Type is required when Crypto Value Box is enabled', 'error');
      return;
    }

    setActionLoading(true);
    try {
      // Prepare data for API
      const submitData = {
        ...formData,
        rateBoxLabel: formData.hasRateBox ? formData.rateBoxLabel : undefined, // Only include if hasRateBox is true
      };

      // Call the API to add new cryptocurrency
      const response = await addCryptoCurrencyAPI(submitData);
      
      // Refresh the currency list
      await fetchCryptoCurrencies();
      
      setAddDialogOpen(false);
      resetForm();
      showSnackbar('Cryptocurrency added successfully', 'success');
    } catch (error) {
      console.error("Error adding cryptocurrency:", error);
      showSnackbar('Failed to add cryptocurrency', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditCurrency = async () => {
    if (!selectedCurrency || !formData.name || !formData.symbol) {
      showSnackbar('Name and Symbol are required', 'error');
      return;
    }

    // // If hasRateBox is true but rateBoxLabel is empty, show warning
    // if (formData.hasRateBox && !formData.rateBoxLabel.trim()) {
    //   showSnackbar('Crypto Type is required when Value Box is enabled', 'error');
    //   return;
    // }

    setActionLoading(true);
    try {
      // Prepare data for update
      const updateData = {
        id: selectedCurrency.id,
        name: formData.name,
        symbol: formData.symbol,
        business_id: selectedCurrency.business_id, // Keep original business_id
        hasRateBox: formData.hasRateBox,
        rateBoxLabel:formData.rateBoxLabel , // Only include if hasRateBox is true
      };

      // Call the API to update cryptocurrency
      const response = await updateCryptoCurrencyAPI(updateData);
      
      // Refresh the currency list
      await fetchCryptoCurrencies();
      
      setEditDialogOpen(false);
      resetForm();
      showSnackbar('Cryptocurrency updated successfully', 'success');
    } catch (error) {
      console.error("Error updating cryptocurrency:", error);
      showSnackbar('Failed to update cryptocurrency', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCurrency = async () => {
    if (!selectedCurrency) return;

    setActionLoading(true);
    try {
      // Call the API to delete cryptocurrency
      const response = await deleteCryptoCurrencyAPI(selectedCurrency.symbol);
      
      // Refresh the currency list
      await fetchCryptoCurrencies();
      
      setDeleteDialogOpen(false);
      showSnackbar('Cryptocurrency deleted successfully', 'success');
    } catch (error) {
      console.error("Error deleting cryptocurrency:", error);
      showSnackbar('Failed to delete cryptocurrency', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const openEditDialog = (currency: CryptoCurrency) => {
    setSelectedCurrency(currency);
    setFormData({
      name: currency.name,
      symbol: currency.symbol,
      business_id: currency.business_id,
      hasRateBox: currency.hasRateBox,
      rateBoxLabel: currency.rateBoxLabel || '', // Set Crypto Type if exists
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (currency: CryptoCurrency) => {
    setSelectedCurrency(currency);
    setFormData({
      name: currency.name,
      symbol: currency.symbol,
      business_id: currency.business_id,
      hasRateBox: currency.hasRateBox,
      rateBoxLabel: currency.rateBoxLabel || '', // Include Value Box info for display
    });
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      symbol: '',
      business_id: 'business_001',
      hasRateBox: true, // Default to having Value Box
      rateBoxLabel: '', // Reset Crypto Type
    });
    setSelectedCurrency(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle Value Box toggle
  const handleRateBoxToggle = (hasRateBox: boolean) => {
    setFormData({
      ...formData,
      hasRateBox,
      // Clear rateBoxLabel when disabling Value Box
      //rateBoxLabel: hasRateBox ? formData.rateBoxLabel : ''
    });
  };

  // Get chip color and icon based on Value Box status
  const getRateBoxChipProps = (hasRateBox: boolean) => {
    if (hasRateBox === 'true' || hasRateBox === true) {
      return {
        color: 'success' as const,
        icon: <CurrencyExchange />,
        label: 'With Value Box'
      };
    } else {
      return {
        color: 'default' as const,
        icon: <MoneyOff />,
        label: 'No Value Box'
      };
    }
  };
const withValueBoxCount = currencies?.filter(currency => currency.hasRateBox === true || currency.hasRateBox === 'true').length;
const withoutValueBoxCount = currencies?.filter(currency => currency.hasRateBox === false || currency.hasRateBox === 'false').length;

  return (
    <IonPage className='pushDown'>
      <IonHeader>
        <IonToolbar>  <MenuButton/>
          <IonTitle>Setup Cryptocurrencies</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Cryptocurrencies Setup
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage cryptocurrencies and configure value box settings
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Cryptocurrencies
                  </Typography>
                  <Typography variant="h4">
                    {currencies?.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    With Value Box
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {withValueBoxCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Without Value Box
                  </Typography>
                  <Typography variant="h4" color="text.secondary">
                    {withoutValueBoxCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search cryptocurrencies by name or symbol..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openAddDialog}
                >
                  Add New Cryptocurrency
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Cryptocurrencies Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    {/* <TableCell>Symbol</TableCell> */}
                    <TableCell>Value Box</TableCell>
                    <TableCell>Crypto Type</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading cryptocurrencies...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCurrencies?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? 'No cryptocurrencies found matching your search' : 'No cryptocurrencies found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCurrencies?.map((currency) => {
                      const chipProps = getRateBoxChipProps(currency.hasRateBox);
                      return (
                        <TableRow key={currency?.id} hover>
                          <TableCell>{currency?.id}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {currency?.name}
                            </Typography>
                          </TableCell>
                          {/* <TableCell>
                            <Chip label={currency?.symbol} size="small" variant="outlined" />
                          </TableCell> */}
                          <TableCell>
                            <Chip 
                              {...chipProps}
                              size="small"
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {currency?.hasRateBox ? (currency?.rateBoxLabel || 'N/A') : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {currency?.dateCreated || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              color="primary"
                              onClick={() => openEditDialog(currency)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => openDeleteDialog(currency)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Add Cryptocurrency Dialog */}
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Add New Cryptocurrency
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setAddDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cryptocurrency Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Bitcoin, Ethereum, Tether"
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Symbol"
                    value={formData.symbol}
                    onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                    placeholder="e.g., BTC, ETH, USDT"
                    helperText="Cryptocurrency symbol (will be converted to uppercase)"
                  />
                </Grid> */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Crypto Type"
                      value={formData.rateBoxLabel}
                      onChange={(e) => setFormData({ ...formData, rateBoxLabel: e.target.value })}
                      placeholder="e.g., BTC, USDT, ETH"
                      helperText="Label to display for the Crypto Value Box input field"
                      required
                    />
                  </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasRateBox}
                        onChange={(e) => handleRateBoxToggle(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Enable Crypto Value Box
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.hasRateBox 
                            ? 'The function of the value box is to input the crypto value e.g 0.001'
                            : 'The function of the value box is to input the crypto value e.g 0.001'
                          }
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                {/* {formData.hasRateBox && (
                
                )} */}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleAddCurrency}
                disabled={actionLoading || !formData.name || (formData.hasRateBox && !formData.rateBoxLabel)}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Adding...' : 'Add Cryptocurrency'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Cryptocurrency Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Edit Cryptocurrency
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setEditDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cryptocurrency Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
             
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Crypto Type"
                      value={formData.rateBoxLabel}
                      onChange={(e) => setFormData({ ...formData, rateBoxLabel: e.target.value })}
                      placeholder="e.g., BTC Rate, Bitcoin Price"
                      helperText="Label to display for the Value Box input field"
                      required
                    />
                  </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasRateBox}
                        onChange={(e) => handleRateBoxToggle(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Enable Value Box
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.hasRateBox 
                            ? 'This cryptocurrency will have a Value Box for price adjustments'
                            : 'This cryptocurrency will not have a Value Box'
                          }
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                 <Grid item xs={12}>
                  <TextField
disabled                    fullWidth
                    label="id"
                    value={formData.symbol}
                    onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                    helperText="Cryptocurrency symbol (will be converted to uppercase)"
                  />
                </Grid>
                {/* {formData.hasRateBox && (
                  
                )} */}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleEditCurrency}
                disabled={actionLoading || !formData.name || !formData.symbol || (formData.hasRateBox && !formData.rateBoxLabel)}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Updating...' : 'Update Cryptocurrency'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Delete Cryptocurrency
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setDeleteDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to delete this cryptocurrency? This action cannot be undone.
              </Alert>
              <Typography variant="body2">
                <strong>Name:</strong> {formData.name}
              </Typography>
              <Typography variant="body2">
                <strong>Symbol:</strong> {formData.symbol}
              </Typography>
              <Typography variant="body2">
                <strong>Value Box:</strong> {formData.hasRateBox ? 'Yes' : 'No'}
              </Typography>
              {formData.hasRateBox && formData.rateBoxLabel && (
                <Typography variant="body2">
                  <strong>Crypto Type:</strong> {formData.rateBoxLabel}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteCurrency}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Deleting...' : 'Delete Cryptocurrency'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default SetupCryptoType;